'use client';

import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import { Country, DEFAULT_COUNTRY, DIAL_CODES, PhoneNumberValue, filterCountries, flagUrl, getCountries } from './countries';

interface PhoneInputProps {
  value: PhoneNumberValue;
  onChange: (value: PhoneNumberValue) => void;
  onPressEnter?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  status?: 'error' | 'warning';
  /** "filled" uses grey fields for white backgrounds; default is white fields for the grey page background */
  variant?: 'filled';
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
}

function Flag({ code }: { code: string }) {
  return <Image className="flag" src={flagUrl(code)} alt="" width={20} height={15} unoptimized />;
}

export default function PhoneInput({ value, onChange, onPressEnter, placeholder, status, variant, disabled, className, id, name }: PhoneInputProps) {
  const t = useTranslations();
  const locale = useLocale();
  const listId = useId();

  const countries = useMemo(() => getCountries(locale), [locale]);
  const country = DIAL_CODES[value.country] ? value.country : DEFAULT_COUNTRY;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const rootRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const numberRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const filtered = useMemo(() => filterCountries(countries, query), [countries, query]);

  // Close when clicking outside the component
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  // Keep the highlighted option visible while navigating with the keyboard
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.children[activeIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const openDropdown = () => {
    if (disabled) return;
    setQuery('');
    setActiveIndex(
      Math.max(
        countries.findIndex((c) => c.code === country),
        0,
      ),
    );
    setOpen(true);
  };

  const closeDropdown = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const selectCountry = (selected: Country) => {
    onChange({ ...value, country: selected.code });
    setOpen(false);
    numberRef.current?.focus();
  };

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setActiveIndex(0);
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      openDropdown();
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filtered[activeIndex]) selectCountry(filtered[activeIndex]);
        break;
      case 'Escape':
        e.preventDefault();
        closeDropdown();
        break;
      case 'Tab':
        setOpen(false);
        break;
    }
  };

  const handleNumberKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') onPressEnter?.(e);
  };

  return (
    <span ref={rootRef} className={['ui-phone-input', variant, status, disabled && 'disabled', open && 'open', className].filter(Boolean).join(' ')}>
      <button
        ref={triggerRef}
        type="button"
        className="country-trigger"
        onClick={() => (open ? setOpen(false) : openDropdown())}
        onKeyDown={handleTriggerKeyDown}
        disabled={disabled}
        aria-label={t('COUNTRYCODE')}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Flag code={country} />
        <span className="dial-code">+{DIAL_CODES[country]}</span>
        <span className="caret" />
      </button>

      <input
        ref={numberRef}
        id={id}
        name={name}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        className="ui-input"
        placeholder={placeholder ?? t('PHONE_NUMBER')}
        value={value.number}
        disabled={disabled}
        onChange={(e) => onChange({ ...value, country, number: e.target.value.replace(/\D/g, '') })}
        onKeyDown={handleNumberKeyDown}
      />

      {open && (
        <div className="country-dropdown">
          <input
            ref={searchRef}
            className="ui-input search"
            placeholder={t('SEARCH')}
            value={query}
            onChange={handleQueryChange}
            onKeyDown={handleSearchKeyDown}
            role="combobox"
            aria-expanded
            aria-controls={listId}
            aria-activedescendant={filtered[activeIndex] ? `${listId}-${filtered[activeIndex].code}` : undefined}
            aria-autocomplete="list"
          />
          <ul ref={listRef} id={listId} className="country-list" role="listbox">
            {filtered.map((c, index) => (
              <li
                key={c.code}
                id={`${listId}-${c.code}`}
                role="option"
                aria-selected={c.code === country}
                className={[index === activeIndex && 'active', c.code === country && 'selected'].filter(Boolean).join(' ')}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectCountry(c)}
              >
                <Flag code={c.code} />
                <span className="name">
                  {c.name} (+{c.dialCode})
                </span>
              </li>
            ))}
            {filtered.length === 0 && <li className="empty">{t('NO_RESULTS')}</li>}
          </ul>
        </div>
      )}
    </span>
  );
}
